import { useRef, useState, useEffect } from 'react';

export const AnimationPhase = {
  Enter: 'enter',
  EnterActive: 'enterActive',
  EnterDone: 'enterDone',
  Exit: 'exit',
  ExitActive: 'exitActive',
  ExitDone: 'exitDone',
} as const;

type AnimationPhaseType = (typeof AnimationPhase)[keyof typeof AnimationPhase];

export type AnimationState<AnimationNameType extends string> = {
  name: AnimationNameType;
  phase?: AnimationPhaseType;
};

type AnimationLifecycle = {
  Init: AnimationPhaseType;
  Active: AnimationPhaseType;
  Done: AnimationPhaseType;
};

type AnimationConfig = {
  duration: number;
};

type TimeoutId = number;

export const useAnimationState = <AnimationNameType extends string>(
  animationConfigMap: Record<AnimationNameType, AnimationConfig>,
  initialAnimationState: AnimationState<AnimationNameType>,
) => {
  const actionQueue = useRef<Array<TimeoutId>>([]);
  const [animationState, setAnimationState] = useState(initialAnimationState);

  const clearActionQueue = () => {
    for (const queuedAction of actionQueue.current) {
      window.clearTimeout(queuedAction);
    }
    actionQueue.current = [];
  };

  const initAnimation = (
    animationName: AnimationNameType,
    animationPhases: AnimationLifecycle,
    onAnimationDone?: () => void,
  ) => {
    clearActionQueue();

    const animationConfig = animationConfigMap[animationName];
    setAnimationState({
      name: animationName,
      phase: animationPhases.Init,
    });

    window.setTimeout(() => {
      setAnimationState({
        name: animationName,
        phase: animationPhases.Active,
      });

      const timeoutId = window.setTimeout(() => {
        setAnimationState({
          name: animationName,
          phase: animationPhases.Done,
        });
        onAnimationDone?.();
      }, animationConfig.duration);

      actionQueue.current.push(timeoutId);
    }, 0);
  };

  const initEnterAnimation = (animationName: AnimationNameType) =>
    initAnimation(animationName, {
      Init: AnimationPhase.Enter,
      Active: AnimationPhase.EnterActive,
      Done: AnimationPhase.EnterDone,
    });

  const initExitAnimation = (
    animationName: AnimationNameType,
    onAnimationDone?: () => void,
  ) =>
    initAnimation(
      animationName,
      {
        Init: AnimationPhase.Exit,
        Active: AnimationPhase.ExitActive,
        Done: AnimationPhase.ExitDone,
      },
      onAnimationDone,
    );

  useEffect(() => {
    return clearActionQueue;
  }, []);

  return { animationState, initEnterAnimation, initExitAnimation };
};
